<template>
  <b-sidebar
    id="near-by-sidebar"
    :visible="isNearBySidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @shown="onShown"
    @change="(val) => $emit('update:is-near-by-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <div>
          <h5 class="mb-0">
            ใกล้เคียง : {{ ownerName }}
          </h5>
          <small class="text-muted">รัศมี 2 กม. · {{ total }} แห่ง</small>
        </div>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- ปุ่มกรองหมวด -->
      <div class="m-2">
        <b-button
          :variant="typeActive === 'all' ? 'primary' : 'outline-primary'"
          pill
          size="sm"
          class="mr-1 mb-1"
          @click="changeType('all')"
        >
          ทั้งหมด
        </b-button>
        <b-button
          v-for="near in categories"
          :key="near.value"
          :variant="typeActive === near.value ? 'primary' : 'outline-primary'"
          pill
          size="sm"
          class="mr-1 mb-1"
          @click="changeType(near.value)"
        >
          {{ near.text }}
          <span v-if="typeActive === 'all' && countByCategory[near.value]">
            ({{ countByCategory[near.value] }})
          </span>
        </b-button>
      </div>

      <!-- เพิ่มสถานที่เข้าหมุด -->
      <div class="mx-2 mb-2">
        <b-button
          v-if="!isAdding"
          variant="outline-secondary"
          size="sm"
          block
          @click="isAdding = true"
        >
          <feather-icon
            icon="PlusIcon"
            size="14"
            class="mr-25"
          />เพิ่มสถานที่
        </b-button>

        <template v-else>
          <b-form-input
            v-model="searchKeyword"
            placeholder="พิมพ์ชื่ออย่างน้อย 2 ตัวอักษร"
            size="sm"
            @input="onSearch"
          />

          <b-list-group
            v-if="searchResults.length"
            class="mt-1"
          >
            <b-list-group-item
              v-for="poi in searchResults"
              :key="poi.id"
              class="d-flex justify-content-between align-items-center py-50"
            >
              <div>
                <div>{{ poiName(poi) }}</div>
                <small class="text-muted">{{ categoryLabel[poi.category] || poi.category }}</small>
              </div>
              <b-button
                size="sm"
                variant="outline-primary"
                :disabled="busyId === poi.id"
                @click="addPoi(poi)"
              >
                เพิ่ม
              </b-button>
            </b-list-group-item>
          </b-list-group>

          <small
            v-else-if="searchKeyword.length >= 2 && !isSearching"
            class="text-muted"
          >
            ไม่พบสถานที่ชื่อนี้ — เพิ่มเข้าฐานก่อนที่เมนู "สถานที่ใกล้เคียง"
          </small>

          <b-button
            variant="flat-secondary"
            size="sm"
            class="mt-1"
            @click="isAdding = false"
          >
            ปิด
          </b-button>
        </template>
      </div>

      <!-- รายการ -->
      <div class="mx-2">
        <div
          v-if="isLoading"
          class="text-center py-2"
        >
          <b-spinner small />
        </div>

        <b-alert
          v-else-if="errorMessage"
          variant="danger"
          show
          class="mb-0"
        >
          <div class="alert-body">
            {{ errorMessage }}
          </div>
        </b-alert>

        <p
          v-else-if="!items.length"
          class="text-muted mb-0"
        >
          ไม่พบสถานที่ใกล้เคียง — ตอนนี้ฐานข้อมูลเก็บเฉพาะกรุงเทพฯ และปริมณฑล
          และต้องบันทึกหมุดซ้ำหนึ่งครั้งหลังย้ายพิกัด
        </p>

        <b-list-group
          v-else
          flush
        >
          <b-list-group-item
            v-for="item in items"
            :key="item.id"
            class="d-flex justify-content-between align-items-center py-50"
          >
            <div>
              <div class="font-weight-bold">
                {{ poiName(item) }}
              </div>
              <small class="text-muted">
                {{ categoryLabel[item.category] || item.category }} ·
                {{ item.distance }} ม. · เดิน {{ item.duration }} นาที
              </small>
              <!-- ของที่คนเพิ่มเอง รอบคำนวณอัตโนมัติจะไม่ลบทิ้ง -->
              <b-badge
                v-if="Number(item.is_manual) === 1"
                variant="light-info"
                class="ml-25"
              >
                เพิ่มเอง
              </b-badge>
            </div>

            <feather-icon
              icon="TrashIcon"
              size="16"
              class="text-danger cursor-pointer"
              @click="removeConfirm(item)"
            />
          </b-list-group-item>
        </b-list-group>
      </div>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BButton, BFormInput, BListGroup, BListGroupItem, BBadge, BSpinner, BAlert,
} from 'bootstrap-vue'
import { ref, computed } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import poiStoreModule from '@/views/poi/poiStoreModule'
import { POI_CATEGORIES } from '@/views/poi/poi-list/usePoiList'

/**
 * แผงข้าง "ใกล้เคียง" — สถานที่ใกล้หมุดในรัศมี 2 กม.
 *
 * อ่านจากตารางที่คำนวณระยะไว้แล้ว (api: POST /poi/v1/nearby-stored) ไม่ได้วัดระยะสดตอนเปิดแผง
 *
 * ⚠️ ของเดิมเรียก `POST /place/v1/near-by` ซึ่งไม่มี route อยู่บน API จริง และ setup() ก็แค่
 * console.log ผลลัพธ์ แผงนี้จึงไม่เคยแสดงรายการเลยตั้งแต่ต้น มีแต่ปุ่มกรอง
 *
 * ── เพิ่ม / ลบ ด้วยมือ ────────────────────────────────────────────────
 * เพิ่มได้แม้อยู่นอกรัศมี และลบของที่ระบบคำนวณมาได้ ฝั่ง API จำการตัดสินใจไว้ด้วยธง
 * is_manual / is_hidden รอบคำนวณอัตโนมัติจึงไม่ลบของที่เพิ่มไว้ และไม่เอาของที่ลบแล้วกลับมา
 *
 * ใช้ได้ทั้งหมุดคอนโดและหมุดบ้าน — ส่ง prop `owner-type` เป็น 'house_pin' สำหรับบ้าน
 */
export default {
  components: {
    BSidebar, BButton, BFormInput, BListGroup, BListGroupItem, BBadge, BSpinner, BAlert,
  },
  model: {
    prop: 'isNearBySidebarActive',
    event: 'update:is-near-by-sidebar-active',
  },
  props: {
    isNearBySidebarActive: {
      type: Boolean,
      required: true,
    },
    placeData: {
      type: Object,
      default: () => ({}),
    },
    ownerType: {
      type: String,
      default: 'place',
    },
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale
    },
    ownerName() {
      const name = (this.placeData && this.placeData.name) || {}

      if (typeof name === 'string') return name

      return name[this.currentLocale] || name.en || Object.values(name).find(Boolean) || ''
    },
  },
  methods: {
    removeConfirm(item) {
      this.$bvModal
        .msgBoxConfirm(`เอา "${this.poiName(item)}" ออกจากสถานที่ใกล้เคียงของหมุดนี้?`, {
          title: 'ยืนยัน',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'เอาออก',
          cancelTitle: 'ยกเลิก',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(value => {
          if (value === true) this.removePoi(item)
        })
    },
  },
  setup(props) {
    const toast = useToast()
    const POI_STORE_MODULE_NAME = 'app-poi'

    if (!store.hasModule(POI_STORE_MODULE_NAME)) store.registerModule(POI_STORE_MODULE_NAME, poiStoreModule)

    const items = ref([])
    const total = ref(0)
    const isLoading = ref(false)
    const errorMessage = ref('')
    const typeActive = ref('all')

    const isAdding = ref(false)
    const searchKeyword = ref('')
    const searchResults = ref([])
    const isSearching = ref(false)
    const busyId = ref(null)

    const categories = POI_CATEGORIES
    const categoryLabel = POI_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.value]: c.text }), {})

    const localeOf = () => {
      /* i18n ไม่ได้ส่งเข้ามาใน setup ของ Vue 2 — อ่านจาก store ของ appConfig แทน */
      const { lang } = document.documentElement

      return lang || 'th'
    }

    const poiName = item => {
      const name = (item && item.name) || {}

      return name[localeOf()] || name.th || name.en || Object.values(name).find(Boolean) || '(ไม่มีชื่อ)'
    }

    const notify = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'danger' ? 'AlertTriangleIcon' : 'CheckIcon', variant },
      })
    }

    const countByCategory = computed(() => items.value.reduce((acc, item) => {
      acc[item.category] = (acc[item.category] || 0) + 1

      return acc
    }, {}))

    const loadData = () => {
      if (!props.placeData || !props.placeData.id) return

      isLoading.value = true
      errorMessage.value = ''

      store
        .dispatch('app-poi/fetchNearbyStored', {
          place_id: props.placeData.id,
          owner_type: props.ownerType,
          categories: typeActive.value === 'all' ? undefined : [typeActive.value],
          limit: 300,
        })
        .then(res => {
          items.value = res.data.data || []
          total.value = res.data.count || items.value.length
        })
        .catch(error => {
          items.value = []
          total.value = 0
          errorMessage.value = (error.response && error.response.data && error.response.data.message)
            || 'โหลดสถานที่ใกล้เคียงไม่สำเร็จ'
        })
        .finally(() => { isLoading.value = false })
    }

    const changeType = value => {
      typeActive.value = value
      loadData()
    }

    /* ค้นจากตาราง poi ทั้งหมด ไม่จำกัดรัศมี — คนเพิ่มของนอกรัศมีได้ */
    let searchTimer = null

    const onSearch = () => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        const keyword = searchKeyword.value.trim()

        if (keyword.length < 2) {
          searchResults.value = []

          return
        }

        isSearching.value = true
        store
          .dispatch('app-poi/fetchData', {
            perpage: 20, offset: 0, keyword, is_active: 1,
          })
          .then(res => { searchResults.value = res.data.data || [] })
          .catch(() => { searchResults.value = [] })
          .finally(() => { isSearching.value = false })
      }, 400)
    }

    const addPoi = poi => {
      busyId.value = poi.id
      store
        .dispatch('app-poi/linkPoi', {
          place_id: props.placeData.id, poi_id: poi.id, owner_type: props.ownerType,
        })
        .then(res => {
          if (!res.data.success) {
            notify(res.data.message || 'เพิ่มไม่สำเร็จ', 'danger')

            return
          }

          notify('เพิ่มแล้ว', 'success')
          searchKeyword.value = ''
          searchResults.value = []
          isAdding.value = false
          loadData()
        })
        .catch(() => notify('เพิ่มไม่สำเร็จ', 'danger'))
        .finally(() => { busyId.value = null })
    }

    const removePoi = item => {
      busyId.value = item.id
      store
        .dispatch('app-poi/unlinkPoi', {
          place_id: props.placeData.id, poi_id: item.id, owner_type: props.ownerType,
        })
        .then(res => {
          if (!res.data.success) {
            notify(res.data.message || 'เอาออกไม่สำเร็จ', 'danger')

            return
          }

          notify('เอาออกแล้ว', 'success')
          loadData()
        })
        .catch(() => notify('เอาออกไม่สำเร็จ', 'danger'))
        .finally(() => { busyId.value = null })
    }

    const onShown = () => {
      typeActive.value = 'all'
      items.value = []
      total.value = 0
      errorMessage.value = ''
      isAdding.value = false
      searchKeyword.value = ''
      searchResults.value = []
      loadData()
    }

    return {
      items,
      total,
      isLoading,
      errorMessage,
      typeActive,
      categories,
      categoryLabel,
      countByCategory,
      isAdding,
      searchKeyword,
      searchResults,
      isSearching,
      busyId,
      poiName,
      changeType,
      onSearch,
      addPoi,
      removePoi,
      onShown,
    }
  },
}
</script>
